<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { TabsTrigger } from 'reka-ui'
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'

export type TabStatusTone = 'success' | 'warning' | 'danger' | 'neutral'

export interface TabDef {
	value: string
	label: string
	/**
	 * Encabezado bajo el que se agrupa la pestaña en la barra lateral. Las
	 * pestañas sin grupo se pintan sueltas, así que agregarlo no rompe a quien no
	 * lo use. El orden de los grupos lo decide el orden del arreglo `tabs`, no un
	 * criterio propio de este componente.
	 */
	group?: string
	/** Estado corto junto al nombre: «Lista», «Faltan 2»… Opcional. */
	status?: { label: string; tone: TabStatusTone }
	/** La pestaña tiene cambios sin guardar: se marca con un punto. */
	dirty?: boolean
}

interface Props {
	tabs: TabDef[]
	defaultTab?: string
	/**
	 * La pestaña activa, para quien necesite moverla desde fuera (por ejemplo,
	 * llevar a la persona a la pestaña del campo con error). Sin `v-model` el
	 * componente la maneja solo, como siempre.
	 */
	modelValue?: string
	/**
	 * Mantiene montadas, ocultas, las pestañas que no se ven. Sirve cuando el
	 * contenido es un solo formulario repartido en pestañas: lo capturado no se
	 * pierde al cambiar de pestaña y la validación alcanza a los campos que no
	 * están a la vista.
	 */
	keepMounted?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	defaultTab: undefined,
	modelValue: undefined,
	keepMounted: false,
})

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const internalTab = ref<string>(
	props.modelValue ?? props.defaultTab ?? props.tabs[0]?.value ?? ''
)

watch(
	() => props.modelValue,
	(value) => {
		if (value !== undefined) internalTab.value = value
	}
)

const activeTab = computed({
	get: () => internalTab.value,
	set: (value: string) => {
		internalTab.value = value
		emit('update:modelValue', value)
	},
})

const STATUS_TONES: Record<TabStatusTone, string> = {
	success: 'bg-emerald-100 text-emerald-700',
	warning: 'bg-amber-100 text-amber-800',
	danger: 'bg-red-100 text-red-700',
	neutral: 'bg-gray-100 text-gray-600',
}

/**
 * Pestañas en bloques consecutivos por grupo, respetando el orden recibido.
 * Se agrupa por tramos y no con un `groupBy`: así dos tramos separados con el
 * mismo nombre siguen siendo dos encabezados, en vez de fusionarse y reordenar
 * la lista por detrás.
 */
const tabSections = computed(() => {
	const sections: { group?: string; tabs: TabDef[] }[] = []

	for (const tab of props.tabs) {
		const last = sections.at(-1)

		if (last && last.group === tab.group) {
			last.tabs.push(tab)
			continue
		}

		sections.push({ group: tab.group, tabs: [tab] })
	}

	return sections
})
</script>

<template>
	<Tabs
		v-model="activeTab"
		:unmount-on-hide="!keepMounted"
		class="flex flex-col lg:flex-row gap-5"
	>
		<!-- ── Desktop sidebar ── -->
		<aside class="hidden lg:block lg:w-56 lg:shrink-0 lg:sticky lg:top-20 self-start">
			<div class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
				<!-- History action (optional) -->
				<div v-if="$slots.history" class="border-b border-gray-100">
					<slot name="history" />
				</div>

				<!-- Identity card (optional) -->
				<div v-if="$slots.identity" class="p-4 border-b border-gray-100">
					<slot name="identity" />
				</div>

				<!-- Tab nav -->
				<TabsList
					class="flex flex-col p-2 gap-0.5 bg-transparent h-auto rounded-none w-full"
				>
					<template
						v-for="(section, index) in tabSections"
						:key="section.group ?? index"
					>
						<p
							v-if="section.group"
							class="w-full px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
							:class="index === 0 ? 'pt-1' : ''"
						>
							{{ section.group }}
						</p>
						<!--
							El TabsTrigger de reka y no el de `ui/tabs`: ése envuelve el contenido
							en un `<span class="truncate">`, y el estado no podía alinearse a la
							derecha. El nombre conserva su `truncate`, como antes.
						-->
						<TabsTrigger
							v-for="tab in section.tabs"
							:key="tab.value"
							:value="tab.value"
							class="inline-flex w-full items-center justify-start gap-2 text-left px-3 py-2.5 rounded-xl text-sm font-medium
								ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
								disabled:pointer-events-none disabled:opacity-50
								text-gray-600 hover:bg-gray-100 hover:text-gray-700
								data-[state=active]:bg-[#253C70] data-[state=active]:text-white data-[state=active]:shadow-sm
								data-[state=active]:hover:bg-[#1e3260] data-[state=active]:hover:text-white"
						>
							<span class="min-w-0 flex-1 truncate">{{ tab.label }}</span>
							<span
								v-if="tab.dirty"
								class="size-2 shrink-0 rounded-full bg-amber-500"
								title="Cambios sin guardar"
							>
								<span class="sr-only">Cambios sin guardar</span>
							</span>
							<span
								v-if="tab.status"
								class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
								:class="STATUS_TONES[tab.status.tone]"
							>
								{{ tab.status.label }}
							</span>
						</TabsTrigger>
					</template>
				</TabsList>
			</div>
		</aside>

		<!-- ── Mobile ── -->
		<div class="lg:hidden space-y-3">
			<!-- Identity (mobile) -->
			<div v-if="$slots['identity-mobile']">
				<slot name="identity-mobile" />
			</div>

			<!-- History action (mobile) -->
			<div v-if="$slots['history-mobile']">
				<slot name="history-mobile" />
			</div>

			<!-- Tab select -->
			<Select v-model="activeTab">
				<SelectTrigger class="w-full bg-white" aria-label="Sección">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup
						v-for="(section, index) in tabSections"
						:key="section.group ?? index"
					>
						<SelectLabel v-if="section.group">{{ section.group }}</SelectLabel>
						<SelectItem
							v-for="tab in section.tabs"
							:key="tab.value"
							:value="tab.value"
						>
							{{ tab.label }}<template v-if="tab.status">
								· {{ tab.status.label }}</template
							><template v-if="tab.dirty"> · sin guardar</template>
						</SelectItem>
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>

		<!-- ── Content ── -->
		<div class="flex-1 min-w-0">
			<TabsContent
				v-for="tab in tabs"
				:key="tab.value"
				:value="tab.value"
				class="mt-0 focus-visible:ring-0 focus-visible:ring-offset-0"
			>
				<slot :name="`tab-${tab.value}`" />
			</TabsContent>
		</div>
	</Tabs>
</template>
